import './Match.css'
import { useEffect, useState, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useCallback } from 'react'

import { useSaveSetMutation, useUpdateMatchMutation } from '../../redux/matchService/matchApiSlice'
import { ReactComponent as Minus } from '../../icon/minus-delete.svg'
import { ReactComponent as Plus } from '../../icon/plus-blue.svg'
import { ButtonWhite } from '../../components/ButtonWhite/ButtonWhite'
import { ReactComponent as Cross } from '../../icon/close.svg'

const loadFirstName = (match) => {
	if (match?.side1_uid === match?.side1?.nodeUid) {
		return match?.side1
	} else if (match?.side1_uid === match?.side2?.nodeUid) {
		return match?.side2
	}
}

const loadSecondName = (match) => {
	if (match?.side2_uid === match?.side2?.nodeUid) {
		return match?.side2
	} else if (match?.side2_uid === match?.side1?.nodeUid) {
		return match?.side1
	}
}

const loadFirstTiebreak = (sets) => {
	if (sets?.length > 0) {
		const tiebreak = sets[sets?.length - 1]?.tie_break_score1
		return tiebreak ? tiebreak : 0
	} else {
		return 0
	}
}

const loadSecondTiebreak = (sets) => {
	if (sets?.length > 0) {
		const tiebreak = sets[sets?.length - 1]?.tie_break_score2
		return tiebreak ? tiebreak : 0
	} else {
		return 0
	}
}

const loadFirstScore = (sets) => {
	if (sets?.length > 0) {
		return sets[sets?.length - 1]?.score1
	} else {
		return 0
	}
}

const loadSecondScore = (sets) => {
	if (sets?.length > 0) {
		return sets[sets?.length - 1]?.score2
	} else {
		return 0
	}
}

function MatchComponent({ match, closeMatch }) {
	const { t } = useTranslation()
	const matchSession = sessionStorage.getItem('match')
	const [updateMatch] = useUpdateMatchMutation()
	const [saveSet] = useSaveSetMutation()
	const tournamentType = useSelector(state => state.tournament.tournament?.type)

	const [firstParticipant, setFirstParticipant] = useState(loadFirstName(match))
	const [secondParticipant, setSecondParticipant] = useState(loadSecondName(match))
	const [scoresSideOne, setScoresSideOne] = useState(loadFirstScore(match?.sets))
	const [scoresSideTwo, setScoresSideTwo] = useState(loadSecondScore(match?.sets))
	const [tiebreakScoresSideOne, setTiebreakScoresSideOne] = useState(loadFirstTiebreak(match?.sets))
	const [tiebreakScoresSideTwo, setTiebreakScoresSideTwo] = useState(loadSecondTiebreak(match?.sets))
	const [tiebreak, setTiebreak] = useState(false)
	const [matchSets, setMatchSets] = useState(match?.sets)
	const [matchState, setMatchState] = useState(match)

	useEffect(() => {
		if (match && Object.keys(match).length === 0 && Object.keys(matchSession).length !== 0) {
			const match = JSON.parse(matchSession)

			setMatchState(match)
			setFirstParticipant(loadFirstName(match))
			setSecondParticipant(loadSecondName(match))
			setMatchSets(match?.sets)
			loadLastResult(match)
		}
	}, [match, matchSession])

	const loadLastResult = (match) => {
		if (match?.sets?.length > 0) {
			const firstTiebreak = match?.sets[match?.sets?.length - 1]?.tie_break_score1
			const secondTiebreak = match?.sets[match?.sets?.length - 1]?.tie_break_score2

			setScoresSideOne(match?.sets[match?.sets?.length - 1]?.score1)
			setScoresSideTwo(match?.sets[match?.sets?.length - 1]?.score2)
			setTiebreakScoresSideOne(firstTiebreak ? firstTiebreak : 0)
			setTiebreakScoresSideTwo(secondTiebreak ? secondTiebreak : 0)
		}
	}

	const handlePlusSideOne = useCallback(() => {
		console.log(tiebreak, '+1')
		if (!tiebreak) {
			console.log('+1')
			setScoresSideOne(prev => prev + 1)
		} else {
			setTiebreakScoresSideOne(prev => prev + 1)
		}
	}, [tiebreak])

	const handlePlusSideTwo = useCallback(() => {
		console.log(tiebreak, '+2')
		if (!tiebreak) {
			console.log('+2')
			setScoresSideTwo(prev => prev + 1)
		} else {
			setTiebreakScoresSideTwo(prev => prev + 1)
		}
	}, [tiebreak])

	const handleMinusSideOne = useCallback(() => {
		console.log(tiebreak, tiebreakScoresSideOne, scoresSideOne, '-1')
		if (!tiebreak && scoresSideOne > 0) {
			console.log('-1')
			setScoresSideOne(prev => prev - 1)
		} else if (tiebreak && tiebreakScoresSideOne > 0) {
			setTiebreakScoresSideOne(prev => prev - 1)
		}
	}, [tiebreak, tiebreakScoresSideOne, scoresSideOne])

	const handleMinusSideTwo = useCallback(() => {
		console.log(tiebreak, scoresSideTwo, tiebreakScoresSideTwo, '-2')
		if (!tiebreak && scoresSideTwo > 0) {
			console.log('-2')
			setScoresSideTwo(prev => prev - 1)
		} else if (tiebreak && tiebreakScoresSideTwo > 0) {
			setTiebreakScoresSideTwo(prev => prev - 1)
		}
	}, [tiebreak, tiebreakScoresSideTwo, scoresSideTwo])

	const addSixSideOne = useCallback(() => {
		console.log(tiebreak, '6-1')
		if (!tiebreak) {
			console.log('6-1')
			setScoresSideOne(6)
		} else {
			setTiebreakScoresSideOne(6)
		}
	}, [tiebreak])

	const addSixSideTwo = useCallback(() => {
		console.log(tiebreak, '6-2')
		if (!tiebreak) {
			console.log('6-2')
			setScoresSideTwo(6)
		} else {
			setTiebreakScoresSideTwo(6)
		}
	}, [tiebreak])

	const activateTiebreak = useCallback(() => {
		setTiebreak(prev => !prev)
	}, [])

	const handleAddSet = async () => {
		const matchData = { ...JSON.parse(matchSession) }
		const newSet = {
			'order_number': matchSets?.length + 1,
			'score1': 0,
			'tie_break_score1': null,
			'score2': 0,
			'tie_break_score2': null
		}

		const sets = [...matchSets, newSet]
		const body = {
			'is_finished': false,
			'sets': sets
		}

		matchData?.sets?.push(newSet)
		sessionStorage.setItem('match', JSON.stringify(matchData))

		try {
			const response = await saveSet({ uid: matchState?.uid, body })

			if (!response?.error) {
				setMatchSets(response?.data?.sets)
				setScoresSideOne(0)
				setScoresSideTwo(0)
				setTiebreakScoresSideOne(0)
				setTiebreakScoresSideTwo(0)
				setTiebreak(false)
			}
		}
		catch (e) {
			console.log('handleRemoveSet catched error', e)
		}
	}

	const handleRemoveSet = async () => {
		const matchData = { ...JSON.parse(matchSession) }
		const sets = [...matchSets]
		sets?.pop()
		const body = {
			'is_finished': false,
			'sets': sets
		}

		matchData?.sets?.pop()
		sessionStorage.setItem('match', JSON.stringify(matchData))

		try {
			const response = await saveSet({ uid: matchState?.uid, body })

			if (!response?.error) {
				setMatchSets(response?.data?.sets)

				if (response?.data?.sets?.length > 0) {
					loadLastResult(response?.data)
				} else {
					setScoresSideOne(0)
					setScoresSideTwo(0)
					setTiebreakScoresSideOne(0)
					setTiebreakScoresSideTwo(0)
				}

				setTiebreak(false)
			}
		}
		catch (e) {
			console.log('handleRemoveSet catched error', e)
		}
	}

	const defineTiebreakForPlayer = useCallback((side) => {
		if (tiebreakScoresSideOne === 0 && tiebreakScoresSideTwo === 0) {
			return null
		} else if (side === 1 && tiebreakScoresSideOne === 0 && tiebreakScoresSideTwo !== 0) {
			return 0
		} else if (side === 2 && tiebreakScoresSideOne !== 0 && tiebreakScoresSideTwo === 0) {
			return 0
		} else if (side === 1) {
			return tiebreakScoresSideOne
		} else {
			return tiebreakScoresSideTwo
		}
	}, [tiebreakScoresSideOne, tiebreakScoresSideTwo])

	const updateLastSet = useCallback(() => {
		const updatedSet = {
			'order_number': matchSets?.length + 1,
			'score1': scoresSideOne,
			'tie_break_score1': defineTiebreakForPlayer(1),
			'score2': scoresSideTwo,
			'tie_break_score2': defineTiebreakForPlayer(2)
		}

		const updatedSets = matchSets?.length > 0 ? [...matchSets.slice(0, -1), updatedSet] : [updatedSet]
		return updatedSets
	}, [scoresSideOne, scoresSideTwo, matchSets, defineTiebreakForPlayer])

	const handleSaveSet = useCallback(async () => {
		const matchData = { ...JSON.parse(matchSession) }
		const sets = updateLastSet()

		matchData.sets = sets
		sessionStorage.setItem('match', JSON.stringify(matchData))

		const body = {
			'is_finished': false,
			'sets': sets
		}

		console.log(body)

		try {
			const response = await saveSet({ uid: matchState?.uid, body })

			if (!response?.error) {
				setMatchSets(response?.data?.sets)
				loadLastResult(response?.data)
			}
		}
		catch (e) {
			console.log('handleSaveSet catched error', e)
		}
	}, [matchState, updateLastSet, saveSet, matchSession])

	const handleFinishMatch = async () => {
		const sets = updateLastSet()

		const body = {
			'is_finished': sets?.length > 0 ? true : false,
			'sets': sets
		}

		try {
			const response = await updateMatch({ uid: matchState?.uid, body })

			if (!response?.error) {
				closeMatch()
				sessionStorage.removeItem('activatedComponent')
				sessionStorage.removeItem('match')
			}
		} catch (e) {
			console.log('updateMatch catched error', e)
		}
	}

	const handleCloseMatch = useCallback(() => {
		setScoresSideOne(0)
		setScoresSideTwo(0)
		setTiebreakScoresSideOne(0)
		setTiebreakScoresSideTwo(0)
		setTiebreak(false)
		sessionStorage.removeItem('match')
		sessionStorage.removeItem('activatedComponent')
		closeMatch()
	}, [closeMatch])

	return (
		<>
			<div className="match__wrapper">
				<div className="match__container">
					<div className="match__icon-cross">
						<Cross onClick={handleCloseMatch} />
					</div>

					<div className="match__set">
						<div className="match__set-score">
							<div className="match__set-score-players">
								<div className="match__set-players__score">
									<div className="match__set-players__score--current">
										<div className="match__set-players__names">
											<p className="match__set-players__name--first">
												{
													tournamentType === 'singles' ?
														firstParticipant?.namePlayer
														:
														`${firstParticipant?.namePlayer1} / ${firstParticipant?.namePlayer2}`
												}
											</p>

											<p className="match__set-players__name--second">
												{
													tournamentType === 'singles' ?
														secondParticipant?.namePlayer
														:
														`${secondParticipant?.namePlayer1} / ${secondParticipant?.namePlayer2}`
												}
											</p>
										</div>

										<div className="match__set-players__scores">
											<p
												className="match__set-players__scores-item--first"
												style={{
													color: tiebreak ? '#CCCCCC' : '#3A3A3A'
												}}
											>
												{scoresSideOne}

												{
													(tiebreak || tiebreakScoresSideOne > 0 || tiebreakScoresSideTwo > 0) &&
													<sup
														className="match__set-players__scores-item--extra"
														style={{
															color: tiebreak ? '#3A3A3A' : '#CCCCCC'
														}}
													>
														{tiebreakScoresSideOne}
													</sup>
												}
											</p>

											<p
												className="match__set-players__scores-item--second"
												style={{
													color: tiebreak ? '#CCCCCC' : '#3A3A3A'
												}}
											>
												{scoresSideTwo}

												{
													(tiebreak || tiebreakScoresSideTwo > 0 || tiebreakScoresSideOne > 0) &&
													<sup
														className="match__set-players__scores-item--extra"
														style={{
															color: tiebreak ? '#3A3A3A' : '#CCCCCC'
														}}
													>
														{tiebreakScoresSideTwo}
													</sup>
												}
											</p>
										</div>
									</div>
								</div>

								<div className="match__set-players__buttons">
									<div className="match__set-players__buttons-item">
										<button
											type="button"
											onClick={handleMinusSideOne}
											className="match__set-players__buttons-minus"
										>
											<Minus className="match__set-players__icon-minus" />
										</button>

										<button
											type="button"
											onClick={handlePlusSideOne}
											className="match__set-players__buttons-plus"
										>
											<Plus />
										</button>
									</div>

									<div className="match__set-players__buttons-item">
										<button
											type="button"
											onClick={handleMinusSideTwo}
											className="match__set-players__buttons-minus"
										>
											<Minus className="match__set-players__icon-minus" />
										</button>

										<button
											type="button"
											onClick={handlePlusSideTwo}
											className="match__set-players__buttons-plus"
										>
											<Plus />
										</button>
									</div>
								</div>
							</div>

							<div className="match__set-buttons">
								<button
									type="button"
									onClick={addSixSideOne}
									className="match__set-button--six"
								>
									6
								</button>

								<button
									type="button"
									onClick={addSixSideTwo}
									className="match__set-button--six"
								>
									6
								</button>
							</div>

							<button
								className={tiebreak ? 'match__set-taibreak--active' : 'match__set-taibreak'}
								onClick={activateTiebreak}
							>
								{t('Taibreak')}
							</button>

							<ButtonWhite type="button" onClick={handleSaveSet} title={t('Save score')} />
						</div>
					</div>

					<div className="match__sets">
						<div className="match__sets-table">
							{
								matchSets?.length !== 0 ?
									matchSets?.map((set, index) =>
										<div
											style={{
												backgroundColor: set?.isActive ? '#EBF4FF' : 'transparent'
											}}
											key={index}
											className="match__sets-table__sets"
										>
											<p className="match__sets-table__sets-scores--first">
												{set?.score1}
												<sup>
													{set?.tie_break_score1 >= 0 ? set?.tie_break_score1 : ''}
												</sup>
											</p>

											<p className="match__sets-table__sets-scores--second">
												{set?.score2}
												<sup>
													{set?.tie_break_score2 >= 0 ? set?.tie_break_score2 : ''}
												</sup>
											</p>
										</div>
									)
									:
									<div
										style={{
											backgroundColor: '#EBF4FF'
										}}
										className="match__sets-table__sets"
									>
										<p className="match__sets-table__sets-scores--first">
											0
										</p>

										<p className="match__sets-table__sets-scores--second">
											0
										</p>
									</div>
							}

							<div className="match__sets-table__buttons">
								<button
									type="button"
									onClick={handleAddSet}
									className="match__sets-table__buttons-set-addition"
								>
									{t('Add set')}
								</button>

								<button
									type="button"
									onClick={handleRemoveSet}
									className="match__sets-table__buttons-set-deletion"
								>
									{t('Remove set')}
								</button>
							</div>

							<button
								onClick={handleFinishMatch}
								className="match__sets-table__button-finish"
							>
								{t('Finish match')}
							</button>
						</div>
					</div>
				</div>
			</div>
		</>
	)
}

export const Match = memo(MatchComponent)